import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { createPortal } from "react-dom";

import "../styles/learning-editor.css";
import EditorForm from "./EditorForm";
import EditorFrame from "./EditorFrame";
import { PreviewScreen, ResultScreen } from "./EditorPreview";
import { ACTION, STEP, gate, initialState, payloadOf, reducer, savedOf, stepIndexOf } from "./editorModel";
import { clearDraft, loadDraft, saveDraft } from "./draftStore";
import { TEXT } from "./editorText";
import { fetchEligibility } from "./editorSubmit";
import { MATCH_STATUS, blockingIssues, matchSummary } from "./matchModel";
import usePrefill from "./usePrefill";
import usePreviewMatch from "./usePreviewMatch";

// While the diagnosis result is being written: check again after these delays (the last one repeats) for up to ~60 s.
const RECORDING_BACKOFF_MS = [2000, 3000, 5000, 8000, 10000];
const RECORDING_WAIT_MS = 60000;

function Footer({ state, dispatch, onClose, onSave, canSave, blocked, saveLabel, matchEnabled }) {
  const allowed = gate(state);
  let hint = allowed.hint && state.step === STEP.FORM ? TEXT.requirements[allowed.hint] : null;
  // a blocked save is explained once, at the top of the preview (PreviewScreen); the footer only says when saving is not wired
  if (state.step === STEP.PREVIEW && !blocked && !canSave) hint = TEXT.notConnected;
  const match = state.step === STEP.PREVIEW ? state.preview.match : null;
  const matching = !!match && match.status !== MATCH_STATUS.IDLE;
  const summary = matching ? matchSummary(match, state.preview.draft) : null;
  // once lines are matched the save is no longer "as new": the footer summary says what is reused, to confirm and new
  const reusing = !!summary && summary.pending === 0 && summary.reused + summary.check > 0;
  return (
    <>
      {state.step === STEP.PREVIEW && (
        <button type="button" className="le-btn" disabled={state.saving} onClick={() => dispatch({ type: ACTION.BACK })}>{TEXT.back}</button>
      )}
      <span className="le-spacer" />
      {hint && <span className="le-hint" id="le-gate-hint">{hint}</span>}
      {matching && (
        <span className={`le-hint${summary && summary.check ? " le-hint--check" : ""}`} aria-live="polite">{summary ? TEXT.match.summary(summary) : ""}</span>
      )}
      {matching && match.status === MATCH_STATUS.ERROR && <span className="le-hint">{TEXT.match.failedHint}</span>}
      {matching && blockingIssues(match).length > 0 && <span className="le-hint le-hint--error" role="alert">{TEXT.match.blocking}</span>}
      {state.error && <span className="le-hint le-hint--error" role="alert">{typeof state.error === "string" ? state.error : TEXT.saveFailed}</span>}
      {state.step === STEP.FORM && (
        <button type="button" className="le-btn le-btn--primary" disabled={!allowed.ok} aria-describedby={allowed.ok ? undefined : "le-gate-hint"}
          onClick={() => dispatch({ type: ACTION.TO_PREVIEW, matching: matchEnabled })}>{TEXT.preview}</button>
      )}
      {state.step === STEP.PREVIEW && (
        <button type="button" className="le-btn le-btn--primary" disabled={!allowed.ok || !canSave || !!blocked} onClick={onSave}>
          {state.saving ? TEXT.saving : (saveLabel || (reusing ? TEXT.match.savePattern : TEXT.save))}
        </button>
      )}
      {state.step === STEP.RESULT && <button type="button" className="le-btn le-btn--primary" onClick={onClose}>{TEXT.done}</button>}
    </>
  );
}

function Screen({ state, dispatch, blocked }) {
  switch (state.step) {
    case STEP.PREVIEW: return <PreviewScreen state={state} blocked={blocked} dispatch={dispatch} />;
    case STEP.RESULT: return <ResultScreen result={state.result} />;
    default: return <EditorForm state={state} dispatch={dispatch} />;
  }
}

/**
 * The learning editor: one form where an engineer teaches Arta what a case was. It shows the category, and asks for the
 * symptoms, the troubleshooting steps taken, the root cause and the fix, mitigation or workaround; a preview follows, then
 * the save. Teach, stuck and unresolved sessions all open this same editor. Plan: Documentation/LEARNING_EDITOR_PLAN.md.
 *
 * Props:
 *   session   {category: {key, label}, symptoms: string[], steps: string[], rootCause?, fixKind?, fixText?}: what is pre-filled.
 *   onSubmit  optional until the server endpoint exists (without it the Save button is off and says so).
 *             async (payload) => ({kind: "learned" | "review" | "refused", reason?}); payload is
 *             {category, symptoms, resolution_steps, root_cause, fix_kind, fix_text}. A rejection shows "could not save" and keeps the preview.
 *   onClose   closes the dialog.
 *   sessionId optional -- when provided, eligibility is checked on open, on Preview and right before Save.
 *   heading   optional string title override (e.g. "Edit pattern" for SME).
 *   saveLabel optional string for the save button (e.g. "Save edit").
 */
export default function LearningEditor({ session, onSubmit, onClose, sessionId, heading, saveLabel }) {
  const [state, dispatch] = useReducer(reducer, session, (s) => initialState(s, loadDraft(sessionId)));
  const sending = useRef(false);
  const [blocked, setBlocked] = useState(null); // reason code string when ineligible
  const live = useRef(true);
  const poll = useRef({ timer: null, started: 0, attempt: 0 });
  usePrefill({ sessionId, dispatch });
  usePreviewMatch({ preview: state.preview, category: state.category, sessionId, dispatch });

  const stopPolling = useCallback(() => {
    clearTimeout(poll.current.timer);
    poll.current = { timer: null, started: 0, attempt: 0 };
  }, []);

  // Ask the server whether this session can be saved now and show the answer. `recording` (the diagnosis just ended and its
  // record is still being written) is checked again with a backoff, for up to RECORDING_WAIT_MS, then reads as slow; any
  // later Preview or Save checks again. Returns the reason code, or null when saving is allowed (or there is no session).
  const check = useCallback(async () => {
    if (!sessionId) return null;
    const result = await fetchEligibility(sessionId);
    if (!live.current) return null;
    const reason = result && !result.canSave ? (result.reason || "review_unavailable") : null;
    if (reason !== "recording") {
      stopPolling();
      setBlocked(reason);
      return reason;
    }
    const p = poll.current;
    if (!p.started) p.started = Date.now();
    const waited = Date.now() - p.started;
    setBlocked(waited >= RECORDING_WAIT_MS ? "recording_slow" : "recording");
    if (waited < RECORDING_WAIT_MS && !p.timer) {
      const delay = RECORDING_BACKOFF_MS[Math.min(p.attempt, RECORDING_BACKOFF_MS.length - 1)];
      p.attempt += 1;
      p.timer = setTimeout(() => { poll.current.timer = null; check(); }, delay);
    }
    return reason;
  }, [sessionId, stopPolling]);

  // On open, and again on every Preview: what was refused when the editor opened may be allowed now.
  useEffect(() => {
    live.current = true;
    return () => { live.current = false; stopPolling(); };
  }, [stopPolling]);
  useEffect(() => {
    if (state.step !== STEP.RESULT) check();
  }, [check, state.step]);

  // Keep the engineer's edits for this chat while they are changing them; a finished save (the result screen) clears them.
  const kept = useMemo(() => (state.step === STEP.RESULT ? null : savedOf(state)),
    [state.step, state.touched, state.draft, state.baseline, state.truncated, state.category]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (kept) saveDraft(sessionId, kept);
  }, [sessionId, kept]);

  const onSave = async () => {
    if (sending.current || typeof onSubmit !== "function") return;
    sending.current = true;
    dispatch({ type: ACTION.SUBMIT_START });
    try {
      // right before the save too: the record the save attaches to may have been written (or decided) since the last check
      if (sessionId && await check()) {
        dispatch({ type: ACTION.SUBMIT_HELD });
        return;
      }
      const result = await onSubmit(payloadOf(state));
      if (result.kind === "refused" && result.reason === "recording") {
        dispatch({ type: ACTION.SUBMIT_HELD });
        check();
        return;
      }
      if (result.kind === "learned" || result.kind === "review") clearDraft(sessionId);
      dispatch({ type: ACTION.SHOW_RESULT, kind: result.kind, reason: result.reason });
    } catch (error) {
      const issue = error && Array.isArray(error.issues) ? error.issues.find((i) => i && i.message) : null;
      dispatch({ type: ACTION.SUBMIT_FAILED, message: issue ? issue.message : null });
    } finally {
      sending.current = false;
    }
  };
  const tree = (
    <EditorFrame stepIndex={stepIndexOf(state.step)} onClose={onClose} heading={heading}
      footer={<Footer state={state} dispatch={dispatch} onClose={onClose} onSave={onSave}
        canSave={typeof onSubmit === "function"} blocked={blocked} saveLabel={saveLabel} matchEnabled={!!sessionId} />}>
      <Screen state={state} dispatch={dispatch} blocked={blocked} />
    </EditorFrame>
  );
  return typeof document === "undefined" ? tree : createPortal(tree, document.body);
}
